import type { MembershipQuery, MembershipRead } from '@pedigreesmart/api-client'
import { annoValidoA, inRegola } from '@pedigreesmart/core'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { useSession } from '../session/SessionProvider.js'
import './people.css'

const PER_PAGINA = 25

/**
 * L'elenco dei tesseramenti.
 *
 * Serve a una cosa che dalla scheda della persona non si può fare: guardare
 * *tutti* insieme. La colonna dello stato è la ragione per cui esiste — chi
 * prepara i solleciti scorre l'elenco e vede chi manca.
 *
 * **Non c'è un filtro «solo i non in regola», e la mancanza è voluta.** Quello
 * stato lo calcola il client dagli anni e dai pagamenti; il server non sa
 * filtrarlo. Un interruttore che nascondesse le righe *della pagina corrente*
 * mostrerebbe «4 tesseramenti» quando ce ne sono quattrocento, e sarebbe una
 * risposta sbagliata data con l'aria di essere giusta. Quando servirà davvero,
 * va aggiunto un filtro sul backend: qui non si può comporre.
 *
 * Il clic apre la scheda della persona, non una schermata del tesseramento:
 * gli anni si aggiungono lì, ed è anche il posto in cui si vede il resto di
 * quello che si sta guardando.
 */
export function MembershipList({ onApri }: { onApri?: ((personaId: number) => void) | undefined }) {
  const { resources } = useSession()
  const [cerca, setCerca] = useState('')
  const [pagina, setPagina] = useState(1)

  const query = useQuery({
    queryKey: ['memberships', 'elenco', cerca, pagina],
    queryFn: ({ signal }) => {
      const filtri: MembershipQuery = { page: pagina, items_per_page: PER_PAGINA }
      const testo = cerca.trim()

      // Una casella sola per due filtri diversi: se sembra un numero di
      // tessera (contiene una cifra) si cerca per numero, altrimenti per
      // cognome. Chiedere all'utente di scegliere il campo prima di digitare
      // sarebbe un gesto in più per una distinzione che si vede da sola.
      if (testo) {
        if (/\d/.test(testo)) filtri.member_number = testo
        else filtri['person.lastName'] = testo
      }

      return resources.memberships.list(filtri, signal)
    },
    placeholderData: keepPreviousData,
  })

  const pagine = query.data ? Math.max(1, Math.ceil(query.data.totalItems / PER_PAGINA)) : 1

  return (
    <section aria-labelledby="titolo-tesseramenti">
      <div className="elenco__testa">
        <h1 id="titolo-tesseramenti">Tesseramenti</h1>
        {query.data && (
          <p className="elenco__conteggio" aria-live="polite">
            {query.data.totalItems === 1
              ? '1 tesseramento'
              : `${query.data.totalItems.toLocaleString('it-IT')} tesseramenti`}
          </p>
        )}
      </div>

      <label className="elenco__ricerca">
        <span className="solo-lettore">Cerca per cognome o numero di tessera</span>
        <input
          type="search"
          value={cerca}
          placeholder="Cognome o numero di tessera"
          onChange={(event) => {
            setCerca(event.target.value)
            setPagina(1)
          }}
        />
      </label>

      {query.isError && (
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere i tesseramenti.
        </p>
      )}

      {query.isPending && <p className="messaggio">Caricamento…</p>}

      {query.data?.member.length === 0 && (
        <p className="messaggio">
          {cerca.trim() ? `Nessun risultato per «${cerca.trim()}».` : 'Nessun tesseramento registrato.'}
        </p>
      )}

      {query.data && query.data.member.length > 0 && (
        <div className="tabella-scorrevole" data-attesa={query.isFetching ? 'si' : undefined}>
          <table>
            <thead>
              <tr>
                <th scope="col">Tessera</th>
                <th scope="col">Socio</th>
                <th scope="col">Iscritto dal</th>
                <th scope="col">Ultimo anno</th>
                <th scope="col">Stato</th>
              </tr>
            </thead>
            <tbody>
              {query.data.member.map((tesseramento) => (
                <Riga key={tesseramento.id} tesseramento={tesseramento} onApri={onApri} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagine > 1 && (
        <nav className="paginazione" aria-label="Pagine">
          <button
            type="button"
            data-variante="secondario"
            disabled={pagina <= 1}
            onClick={() => {
              setPagina((p) => p - 1)
            }}
          >
            Precedente
          </button>
          <span aria-live="polite">
            Pagina {pagina} di {pagine}
          </span>
          <button
            type="button"
            data-variante="secondario"
            disabled={pagina >= pagine}
            onClick={() => {
              setPagina((p) => p + 1)
            }}
          >
            Successiva
          </button>
        </nav>
      )}
    </section>
  )
}

function Riga({
  tesseramento,
  onApri,
}: {
  tesseramento: MembershipRead
  onApri?: ((personaId: number) => void) | undefined
}) {
  const anni = tesseramento.years ?? []
  const oggi = new Date()
  const corrente = annoValidoA(anni, oggi)
  const regolare = inRegola(anni, oggi)
  const ultimo = anni.reduce<number | null>((piu, anno) => Math.max(piu ?? 0, anno.year), null)

  const persona = tesseramento.person?.id
  const apribile = typeof onApri === 'function' && typeof persona === 'number'

  return (
    <tr
      className={apribile ? 'riga--apribile' : undefined}
      onClick={
        apribile
          ? () => {
              onApri(persona)
            }
          : undefined
      }
    >
      <td className="codice">{tesseramento.member_number}</td>
      <td>
        <span className="riga__nome">{tesseramento.person?.display_name ?? '—'}</span>
        {tesseramento.left_on && <span className="riga__secondario">Uscito</span>}
      </td>
      <td>{data(tesseramento.joined_on)}</td>
      <td>{ultimo ?? '—'}</td>
      <td>
        {/* Testo oltre al colore: è l'informazione su cui si decide se
            mandare un sollecito, e il colore da solo escluderebbe chi non lo
            distingue. */}
        <span className="stato" data-regola={regolare ? 'si' : 'no'}>
          {regolare ? `In regola${corrente ? ` (${String(corrente.year)})` : ''}` : 'Non in regola'}
        </span>
      </td>
    </tr>
  )
}

function data(iso?: string | null): string {
  if (!iso) return '—'

  const quando = new Date(iso)

  return Number.isNaN(quando.getTime()) ? '—' : quando.toLocaleDateString('it-IT')
}
